<template>
  <div class="main-wrapper print-page-ff flex min-h-screen flex-col">
    <VcHeader />

    <!-- Main Content -->
    <main id="maincontent" class="main flex grow flex-col items-center py-5 print:py-0">
      <slot />
    </main>

    <VcFooter />
  </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import { onMounted, onUnmounted } from "vue";
import { Logger } from "@/core/utilities";
import { useThorlabsGtm } from "@/shared/gtm/gtm";
import { VcFooter, VcHeader } from "@/shared/layout";

const { gtmSendGenerateLeadEvent } = useThorlabsGtm();

/**
 * This event listeners are attached here to handle clicks on <a> tags with mailto: or tel: links that should trigger generate lead event
 * Some of these links are generated using translation, so these event cannot be directly attached there.
 * The function is debounced to ensure all (or most) elements are rendered before trying to find links.
 */
const attachClickListenersToLeadGeneratingLinksDebounced = useDebounceFn(() => {
  const aElements = document.querySelectorAll("a[data-gtm-lead-source]");

  aElements.forEach((linkElement) => {
    Logger.debug("Attaching link event listener");
    linkElement.addEventListener("click", () => {
      const leadSource = linkElement.getAttribute("data-gtm-lead-source");
      if (leadSource) {
        gtmSendGenerateLeadEvent(leadSource);
      }
    });
  });
}, 1000);

const handleKeyDown = (e: KeyboardEvent) => {
  if (e.key === "Tab") {
    document.body.classList.add("tab-user");
  }
};

const handleMouseDown = () => {
  document.body.classList.remove("tab-user");
};

onMounted(() => {
  window.addEventListener("keydown", handleKeyDown);
  window.addEventListener("mousedown", handleMouseDown);

  void attachClickListenersToLeadGeneratingLinksDebounced();
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyDown);
  window.removeEventListener("mousedown", handleMouseDown);
});
</script>

<style scoped lang="scss">
#maincontent {
  &:has(.create-account-form, .log-in, .reset-password, .recover-account, .forgot-password, .welcome, .success) {
    @apply bg-[--color-neutral-a6];
  }

  &:has(.account-wrapper) {
    @apply py-0;
  }
  &:has(.careers-page) {
    @apply pt-0;
  }
  &:has(.cart) {
    @apply pt-4;
  }

  &:has(.mobile-lab-page, .mobile-lab-resources-page, .new-lab-discount-page) {
    @apply pt-0;
  }
  &:has(.welcome) {
    @apply justify-start;
  }

  &:has(.home-page):not(:has(.home-page .alert-block)) {
    @apply pt-0;
  }
}
</style>
